import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBranchAnalyticsThunk } from "../../../store/thunks/branchThunks";
import {
  CurrencyRupeeIcon,
  UserGroupIcon,
  TrendingUpIcon,
  ClockIcon,
} from "@heroicons/react/outline";

const BranchAnalytics = ({ branchId }) => {
  const dispatch = useDispatch();
  const { branchAnalytics, loading } = useSelector((state) => state.branch);

  useEffect(() => {
    if (branchId) {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);

      dispatch(
        getBranchAnalyticsThunk({
          branchId,
          dateRange: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
        })
      );
    }
  }, [dispatch, branchId]);

  if (loading || !branchAnalytics) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-32 bg-gray-200 rounded"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  const metrics = [
    {
      title: "Total Revenue",
      value: `₹${(
        branchAnalytics.performance?.totalRevenue || 0
      ).toLocaleString()}`,
      icon: CurrencyRupeeIcon,
      color: "bg-green-100 text-green-600",
    },
    {
      title: "Total Customers",
      value: branchAnalytics.performance?.totalCustomers || 0,
      icon: UserGroupIcon,
      color: "bg-blue-100 text-blue-600",
    },
    {
      title: "Target Achievement",
      value: `${branchAnalytics.efficiency?.targetAchievement || 0}%`,
      icon: TrendingUpIcon,
      color: "bg-yellow-100 text-yellow-600",
    },
    {
      title: "Weekly Hours",
      value: `${branchAnalytics.operationalHours?.weeklyHours || 0} hrs`,
      icon: ClockIcon,
      color: "bg-purple-100 text-purple-600",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-lg ${metric.color}`}>
                <metric.icon className="h-6 w-6" />
              </div>
            </div>
            <h3 className="text-2xl font-bold">{metric.value}</h3>
            <p className="text-sm text-gray-600 mt-1">{metric.title}</p>
          </div>
        ))}
      </div>

      {/* Monthly Performance */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Monthly Performance</h3>
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Target Achievement</span>
              <span className="font-semibold">
                ₹
                {(
                  branchAnalytics.performance?.currentMonthAchievement || 0
                ).toLocaleString()}{" "}
                / ₹
                {(
                  branchAnalytics.performance?.monthlyTarget || 0
                ).toLocaleString()}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{
                  width: `${Math.min(
                    100,
                    parseFloat(
                      branchAnalytics.efficiency?.targetAchievement || 0
                    )
                  )}%`,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Rate Configuration Impact */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Rate Configuration</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-600">Policy</p>
            <p className="font-semibold">
              {branchAnalytics.rateConfig?.ratePolicy}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Mode</p>
            <p className="font-semibold">
              {branchAnalytics.rateConfig?.rateMode}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Auto Sync</p>
            <p className="font-semibold">
              {branchAnalytics.rateConfig?.autoSync?.enabled
                ? "Enabled"
                : "Disabled"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Working Days</p>
            <p className="font-semibold">
              {branchAnalytics.operationalHours?.workingDays}/7
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchAnalytics;
